/**
 * `#/case/{matter_id}` and `#/case/{matter_id}/{run_id}` — read off the hash.
 *
 * Its own module, and not a helper inside `CaseScreen.tsx`, for the reason the
 * lint rule states: a file that exports both a component and a function loses
 * fast refresh. It is also the piece worth testing without rendering anything —
 * a deep link to a specific run is how one person sends another the evidence
 * for "look at what stage s2 stopped at", so it has to survive a trailing
 * slash and a missing segment rather than resolving to a different matter.
 */

export interface CasePath {
  matterId: string | null
  runId: string | null
}

/**
 * Decode one path segment, or hand back what was there.
 *
 * `decodeURIComponent` throws `URIError` on a malformed percent sequence —
 * `%`, `%Z`, `%E0%A4` — and this function is called during render, from
 * `CaseScreen`'s `useMemo`. An uncaught throw there unmounts the tree and the
 * whole application goes blank: a truncated link pasted into chat, or a URL a
 * mail client mangled, takes down every screen rather than the one route it
 * addresses. Adversarial review found it; nothing in the suite covered a
 * malformed sequence because every fixture used a well-formed one.
 *
 * The undecoded segment is the right fallback. A matter id is `GW-YYYY-NNNN`
 * and a run id is a uuid, so neither ever legitimately needs decoding — a
 * segment that fails to decode was never going to name a real matter, and
 * passing it through makes it fail as "no such matter", which is a page that
 * says what is wrong. Returning `null` instead would silently redirect a
 * mangled deep link to the whole docket, which is the "resolves to a different
 * matter" failure the decode exists to prevent.
 */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * Split a route path into the matter and the optional run it pins.
 *
 * Both segments are decoded. A matter id is `GW-YYYY-NNNN` and a run id is a
 * uuid, so neither should ever need decoding — doing it anyway means a mangled
 * link fails as "no such matter" rather than silently resolving to a different
 * one.
 */
export function parseCasePath(path: string): CasePath {
  const parts = path.replace(/^\/+/, '').split('/')
  const matterId = parts[1] ? decodeSegment(parts[1]) : ''
  const runId = parts[2] ? decodeSegment(parts[2]) : ''
  return { matterId: matterId || null, runId: runId || null }
}
